import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { normalizeText } from '../extensions/CustomHighlight';

const LOG = (...args) => console.log('[useComments]', ...args);

/**
 * useComments — centralized comment logic.
 *
 * Marks in the document always keep their original tempId (never renamed).
 * State tracks permanentId. The `markIdMap` ref maps permanentId → tempId
 * so mark operations can find the right marks.
 */
export function useComments({ editor, socket, contractId, currentUser }) {
  const [comments, setComments] = useState([]);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [pendingComment, setPendingCommentState] = useState(null);
  const isMounted = useRef(true);
  // Track temp IDs awaiting server ack — reconciliation skips these
  const pendingTempIds = useRef(new Set());
  // Store comments removed by undo so redo can restore them
  const undoTrash = useRef(new Map());
  // Maps permanentId → tempId (the ID actually in the document mark)
  const markIdMap = useRef(new Map());

  // Helper: get the mark ID in the document for a given comment ID (permanentId → tempId)
  // Follows the chain: newPermId → oldPermId → tempId
  const getMarkId = (commentId) => {
    let id = commentId;
    while (markIdMap.current.has(id)) {
      id = markIdMap.current.get(id);
    }
    return id;
  };

  // Helper: reverse lookup — get the permanent ID for a mark ID (tempId → permanentId)
  const resolveMarkId = useCallback((markId) => {
    for (const [permId, tempId] of markIdMap.current.entries()) {
      if (tempId === markId) return permId;
    }
    return markId; // no mapping = id is already correct (pre-ack or same)
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ── Socket event handling ─────────────────────────────────────────────────

  useEffect(() => {
    if (!socket || !editor) return;

    const handleCommentEvent = (data) => {
      if (!isMounted.current) return;
      const { type } = data;
      LOG('socket event:', type, data);

      if (type === 'sync') {
        const synced = data.comments || [];
        setComments(synced);

        // Reconcile marks for system (AI) comments that exist in DB but not in the document
        setTimeout(() => {
          if (!editor || !isMounted.current) return;
          const doc = editor.state?.doc;
          if (!doc) return;

          // Collect all commentIds that already have marks in the document
          const existingMarkIds = new Set();
          doc.descendants((node) => {
            if (node.marks) {
              node.marks.forEach(mark => {
                if (mark.type.name === 'commentMark' && mark.attrs.commentId) {
                  existingMarkIds.add(mark.attrs.commentId);
                }
              });
            }
          });

          for (const comment of synced) {
            if (!comment.is_system) continue;
            if (existingMarkIds.has(comment.id)) continue;
            if (!comment.contextText) continue;

            // Find contextText in the document and apply the mark
            const searchText = normalizeText(comment.contextText);
            if (searchText.length < 3) continue;

            const docText = doc.textContent;
            const normalizedDoc = normalizeText(docText);
            const idx = normalizedDoc.toLowerCase().indexOf(searchText.toLowerCase());
            if (idx === -1) continue;

            // Map normalized index to doc position
            let charCount = 0;
            let fromPos = null;
            let toPos = null;

            doc.descendants((node, pos) => {
              if (fromPos !== null && toPos !== null) return false;
              if (node.isText) {
                const nodeStart = charCount;
                const nodeEnd = charCount + node.text.length;

                // Check if our target range overlaps this text node
                if (fromPos === null && nodeEnd > idx) {
                  fromPos = pos + (idx - nodeStart);
                }
                if (fromPos !== null && toPos === null && nodeEnd >= idx + searchText.length) {
                  toPos = pos + (idx + searchText.length - nodeStart);
                }

                charCount += node.text.length;
              } else if (node.isBlock && !node.isTextblock) {
                // Add space for block separators in normalized text
                charCount += 1;
              }
            });

            if (fromPos !== null && toPos !== null) {
              editor.chain()
                .setTextSelection({ from: fromPos, to: toPos })
                .addCommentMark(comment.id)
                .run();
              LOG('Applied mark for system comment:', comment.id, comment.contextText.slice(0, 40));
            }
          }

          // Restore selection to start
          editor.commands.setTextSelection(0);
        }, 1000); // Wait for Yjs content sync
      } else if (type === 'add') {
        const comment = data.comment;
        // Server broadcasts temp_id alongside the permanent comment.id
        // so remote clients can map permanentId → tempId (mark in doc uses tempId via Yjs)
        if (data.temp_id && data.temp_id !== comment.id && !markIdMap.current.has(comment.id)) {
          markIdMap.current.set(comment.id, data.temp_id);
          LOG('remote add: mapped', comment.id, '→', data.temp_id);
        }
        setComments(prev => {
          const exists = prev.find(c => c.id === comment.id);
          if (exists) return prev.map(c => c.id === comment.id ? comment : c);
          return [comment, ...prev];
        });
      } else if (type === 'resolve') {
        const { commentId, resolved } = data;
        setComments(prev =>
          prev.map(c => c.id === commentId ? { ...c, resolved } : c)
        );
        if (editor) editor.commands.setCommentResolved(getMarkId(commentId), resolved);
      } else if (type === 'delete') {
        const { commentId } = data;
        // If the mark is still in the doc (redo restored it), ignore the stale delete
        const mid = getMarkId(commentId);
        let markExists = false;
        if (editor) {
          editor.state.doc.descendants((node) => {
            if (!node.isText || markExists) return;
            node.marks.forEach(mark => {
              if (mark.type.name === 'comment' && mark.attrs.commentId === mid) markExists = true;
            });
          });
        }
        if (markExists) {
          LOG('socket delete IGNORED (mark still in doc):', commentId);
          return;
        }
        setComments(prev => prev.filter(c => c.id !== commentId));
        if (editor) editor.commands.removeCommentMark(mid);
      } else if (type === 'reply') {
        const { commentId, reply } = data;
        setComments(prev =>
          prev.map(c =>
            c.id === commentId
              ? { ...c, replies: [...(c.replies || []), reply] }
              : c
          )
        );
      } else if (type === 'delete_reply') {
        const { commentId, replyId } = data;
        setComments(prev =>
          prev.map(c =>
            c.id === commentId
              ? { ...c, replies: (c.replies || []).filter(r => r.id !== replyId) }
              : c
          )
        );
      } else if (type === 'update') {
        const { commentId, text } = data;
        setComments(prev =>
          prev.map(c => c.id === commentId ? { ...c, text } : c)
        );
      }
    };

    const handleAck = (data) => {
      if (!isMounted.current || !data.temp_id || !data.permanent_id) return;
      LOG('ack:', data.temp_id, '→', data.permanent_id);
      pendingTempIds.current.delete(data.temp_id);
      markIdMap.current.set(data.permanent_id, data.temp_id);

      // Check if the mark still exists (undo may have removed it before ack arrived)
      let markExists = false;
      if (editor) {
        editor.state.doc.descendants((node) => {
          if (!node.isText || markExists) return;
          node.marks.forEach(mark => {
            if (mark.type.name === 'comment' && mark.attrs.commentId === data.temp_id) markExists = true;
          });
        });
      }
      if (!markExists) {
        LOG('ack: mark already gone (undone), saving to undoTrash:', data.permanent_id);
        setComments(prev => {
          const entry = prev.find(c => c.id === data.temp_id || c.id === data.permanent_id);
          if (entry) {
            undoTrash.current.set(data.permanent_id, { ...entry, id: data.permanent_id });
          }
          return prev.filter(c => c.id !== data.temp_id && c.id !== data.permanent_id);
        });
        return;
      }

      setComments(prev => {
        const hasPermanent = prev.some(c => c.id === data.permanent_id);
        if (hasPermanent) {
          return prev.filter(c => c.id !== data.temp_id);
        }
        return prev.map(c => c.id === data.temp_id ? { ...c, id: data.permanent_id } : c);
      });
    };

    socket.on('comment', handleCommentEvent);
    socket.on('comment_ack', handleAck);

    socket.emit('comment', { type: 'request_sync', contract_id: contractId });

    return () => {
      socket.off('comment', handleCommentEvent);
      socket.off('comment_ack', handleAck);
    };
  }, [socket, editor, contractId]);

  // ── Reconcile hook state with editor marks on undo/redo ──────────────────
  const reconcile = useCallback(() => {
    if (!editor) return;

    LOG('reconcile: START');

    // Collect all comment mark IDs in the document (these are tempIds)
    const docMarkIds = new Map(); // markId → resolved
    editor.state.doc.descendants((node) => {
      if (!node.isText) return;
      node.marks.forEach(mark => {
        if (mark.type.name === 'comment') {
          const id = mark.attrs?.commentId;
          if (id && id !== 'pending') {
            docMarkIds.set(id, !!mark.attrs?.resolved);
          }
        }
      });
    });

    LOG('reconcile: marks in doc:', [...docMarkIds.keys()]);
    LOG('reconcile: markIdMap:', [...markIdMap.current.entries()]);

    const orphanedIds = [];
    const restoredComments = [];

    setComments(prev => {
      LOG('reconcile: comments in state:', prev.map(c => c.id));
      let changed = false;

      // Build reverse map: tempId → permanentId (for redo detection)
      const tempToPermanent = new Map();
      markIdMap.current.forEach((tempId, permId) => {
        tempToPermanent.set(tempId, permId);
      });

      // Detect marks in doc with no state entry (redo restored marks)
      const stateIds = new Set(prev.map(c => c.id));
      // Also index by markId
      const stateMarkIds = new Set();
      prev.forEach(c => {
        const mid = markIdMap.current.get(c.id) || c.id;
        stateMarkIds.add(mid);
      });

      docMarkIds.forEach((resolved, markId) => {
        if (stateMarkIds.has(markId)) return; // already matched
        if (pendingTempIds.current.has(markId)) return;

        // Mark in doc but not in state — check undoTrash
        // undoTrash is keyed by permanentId
        const permanentId = tempToPermanent.get(markId);
        const trashKey = permanentId || markId;
        const trashed = undoTrash.current.get(trashKey);
        if (trashed) {
          LOG('reconcile: RESTORE from undoTrash:', trashKey, '(markId:', markId, ')');
          changed = true;
          restoredComments.push({ ...trashed, resolved });
          undoTrash.current.delete(trashKey);
        }
      });

      const next = [
        ...restoredComments,
        ...prev.filter(c => {
          if (pendingTempIds.current.has(c.id)) {
            LOG('reconcile: SKIP (pendingTemp):', c.id);
            return true;
          }

          // Check if this comment's mark exists in the document
          const mid = markIdMap.current.get(c.id) || c.id;
          if (docMarkIds.has(mid)) {
            LOG('reconcile: KEEP (in doc):', c.id, '(markId:', mid, ')');
            return true;
          }

          LOG('reconcile: ORPHANED (mark gone):', c.id, '(markId:', mid, ')');
          changed = true;
          orphanedIds.push(c.id);
          undoTrash.current.set(c.id, c);
          return false;
        }).map(c => {
          const mid = markIdMap.current.get(c.id) || c.id;
          if (docMarkIds.has(mid) && c.resolved !== docMarkIds.get(mid)) {
            LOG('reconcile: SYNC resolved:', c.id, c.resolved, '→', docMarkIds.get(mid));
            changed = true;
            return { ...c, resolved: docMarkIds.get(mid) };
          }
          return c;
        })
      ];
      LOG('reconcile: changed=', changed, 'orphaned=', orphanedIds, 'restored=', restoredComments.map(c => c.id));
      return changed ? next : prev;
    });

    orphanedIds.forEach(id => {
      LOG('reconcile: emitting delete for orphaned:', id);
      socket?.emit('comment', {
        type: 'delete',
        contract_id: contractId,
        commentId: id,
      });
    });

    restoredComments.forEach(c => {
      LOG('reconcile: emitting re-add for restored:', c.id);
      pendingTempIds.current.add(c.id);
      socket?.emit('comment', {
        type: 'add',
        contract_id: contractId,
        comment: { id: c.id, text: c.text, contextText: c.contextText },
      });
    });

    LOG('reconcile: END');
  }, [editor, socket, contractId]);

  // Trigger reconciliation on keyboard undo/redo
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        LOG('keydown detected:', event.shiftKey ? 'REDO' : 'UNDO');
        setTimeout(reconcile, 50);
      }
    };

    const dom = editor.view.dom;
    dom.addEventListener('keydown', handleKeyDown);
    return () => dom.removeEventListener('keydown', handleKeyDown);
  }, [editor, reconcile]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const setPendingComment = useCallback((pendingData) => {
    LOG('setPendingComment:', pendingData ? { from: pendingData.from, to: pendingData.to } : null);
    if (editor) {
      // Pending highlight is UI-only — must not enter the undo stack
      const { tr } = editor.state;
      const markType = editor.state.schema.marks.comment;
      editor.state.doc.descendants((node, pos) => {
        if (!node.isText) return;
        node.marks.forEach(mark => {
          if (mark.type === markType && mark.attrs.commentId === 'pending') {
            tr.removeMark(pos, pos + node.nodeSize, markType);
          }
        });
      });
      if (pendingData && pendingData.from !== undefined && pendingData.to !== undefined) {
        tr.addMark(pendingData.from, pendingData.to, markType.create({ commentId: 'pending', resolved: false }));
      }
      tr.setMeta('addToHistory', false);
      editor.view.dispatch(tr);
    }
    setPendingCommentState(pendingData);
  }, [editor]);

  const addComment = useCallback((text, pendingData) => {
    if (!editor || !socket || !currentUser) return;

    const contextText = typeof pendingData === 'string'
      ? pendingData
      : pendingData?.selectedText || '';
    const from = typeof pendingData === 'object' ? pendingData?.from : undefined;
    const to   = typeof pendingData === 'object' ? pendingData?.to   : undefined;

    const tempId = uuidv4();
    pendingTempIds.current.add(tempId);
    const now = new Date().toISOString();

    LOG('addComment: tempId=', tempId, 'from=', from, 'to=', to, 'text=', text);

    // Remove pending mark (non-undoable)
    {
      const { tr } = editor.state;
      const markType = editor.state.schema.marks.comment;
      let hasPending = false;
      editor.state.doc.descendants((node, pos) => {
        if (!node.isText) return;
        node.marks.forEach(mark => {
          if (mark.type === markType && mark.attrs.commentId === 'pending') {
            tr.removeMark(pos, pos + node.nodeSize, markType);
            hasPending = true;
          }
        });
      });
      if (hasPending) {
        tr.setMeta('addToHistory', false);
        editor.view.dispatch(tr);
      }
    }

    // Add real mark (this IS undoable — single undo step)
    if (from !== undefined && to !== undefined) {
      editor.chain()
        .setTextSelection({ from, to })
        .addCommentMark(tempId)
        .run();
    } else {
      editor.commands.addCommentMark(tempId);
    }
    // Mark added synchronously — safe to remove from pendingTempIds
    // so reconcile can detect undo removal
    pendingTempIds.current.delete(tempId);

    const newComment = {
      id: tempId,
      text,
      contextText,
      resolved: false,
      timestamp: now,
      user: {
        id: currentUser.id,
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        email: currentUser.email,
      },
      replies: [],
    };

    setComments(prev => [newComment, ...prev]);

    socket.emit('comment', {
      type: 'add',
      contract_id: contractId,
      comment: { id: tempId, text, contextText },
    });

    setPendingCommentState(null);
  }, [editor, socket, currentUser, contractId]);

  const resolveComment = useCallback((commentId, resolved = true) => {
    if (!socket) return;
    LOG('resolveComment:', commentId, 'markId:', getMarkId(commentId));
    setComments(prev =>
      prev.map(c => c.id === commentId ? { ...c, resolved } : c)
    );
    if (editor) editor.commands.setCommentResolved(getMarkId(commentId), resolved);
    socket.emit('comment', {
      type: 'resolve',
      contract_id: contractId,
      commentId,
      resolved,
    });
  }, [socket, editor, contractId]);

  const deleteComment = useCallback((commentId) => {
    if (!socket) return;
    LOG('deleteComment:', commentId, 'markId:', getMarkId(commentId));
    setComments(prev => prev.filter(c => c.id !== commentId));
    if (editor) editor.commands.removeCommentMark(getMarkId(commentId));
    socket.emit('comment', {
      type: 'delete',
      contract_id: contractId,
      commentId,
    });
  }, [socket, editor, contractId]);

  const updateComment = useCallback((commentId, text) => {
    if (!socket) return;
    setComments(prev =>
      prev.map(c => c.id === commentId ? { ...c, text } : c)
    );
    socket.emit('comment', {
      type: 'update',
      contract_id: contractId,
      commentId,
      text,
    });
  }, [socket, contractId]);

  const replyToComment = useCallback((commentId, text) => {
    if (!socket || !currentUser) return;
    socket.emit('comment', {
      type: 'reply',
      contract_id: contractId,
      commentId,
      reply: { text },
    });
  }, [socket, currentUser, contractId]);

  const deleteReply = useCallback((commentId, replyId) => {
    if (!socket) return;
    setComments(prev =>
      prev.map(c =>
        c.id === commentId
          ? { ...c, replies: (c.replies || []).filter(r => r.id !== replyId) }
          : c
      )
    );
    socket.emit('comment', {
      type: 'delete_reply',
      contract_id: contractId,
      commentId,
      replyId,
    });
  }, [socket, contractId]);

  return {
    comments,
    activeCommentId,
    pendingComment,
    setActiveCommentId,
    setPendingComment,
    addComment,
    resolveComment,
    deleteComment,
    updateComment,
    replyToComment,
    deleteReply,
    reconcileAfterUndoRedo: reconcile,
    resolveMarkId,
    getMarkId,
  };
}
